import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

// Short-lived IP cache so repeated getIpAddress() calls within a short window
// don't each hit ip.ticca.com. Uses in-memory state with a TTL and re-entrancy
// protection so concurrent callers share a single in-flight request.
const IP_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

let ipCacheValue: string | null = null;
let ipCacheTime = 0;
let inFlight: Promise<string> | null = null;

export const getIpAddress = async (): Promise<string> => {
  const now = Date.now();
  if (ipCacheValue !== null && now - ipCacheTime < IP_CACHE_TTL_MS) {
    return ipCacheValue;
  }

  if (inFlight) {
    return inFlight;
  }

  inFlight = (async (): Promise<string> => {
    try {
      const baseURL = "https://ip.ticca.com/";
      const res = await axios.get(baseURL);
      const value: string = res.data ?? "";
      ipCacheValue = value;
      ipCacheTime = Date.now();
      // Mirror the existing convention of persisting the IP to localStorage
      // (see LocalStorageConstants.ipAddress) for cross-session reuse.
      try {
        localStorage.setItem("ip_address", value);
      } catch {
        // storage may be unavailable; cache value is still valid in-memory
      }
      return value;
    } catch (err) {
      // Fall back to whatever we previously cached, even if stale.
      if (ipCacheValue !== null) {
        return ipCacheValue;
      }
      return "";
    }
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
};

/**
 * Forces the next getIpAddress() call to refetch from ip.ticca.com.
 */
export const invalidateIpAddress = () => {
  ipCacheValue = null;
  ipCacheTime = 0;
};